import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function getAdminToken() {
  return localStorage.getItem("gg_admin_token") || "";
}

export async function adminFetch(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAdminToken()}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem("gg_admin_token");
    window.location.href = "/admin/login";
    throw new Error("Unauthorized");
  }
  return res;
}

export default function AdminLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
        return;
      }
      localStorage.setItem("gg_admin_token", data.token);
      navigate("/admin", { replace: true });
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="grain relative flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center">
          <img src={IMAGES.logo.src} alt="Glam Girl Makeup Studio" className="mx-auto h-16 w-16 rounded-full border border-champagne/30 object-cover" />
          <p className="mt-4 font-serif text-3xl font-medium italic text-ivory">Glam Girl</p>
          <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.34em] text-champagne">Owner Dashboard</p>
        </div>

        <form onSubmit={submit} className="mt-9 space-y-4 rounded-2xl border border-ivory/10 bg-white/[0.03] p-7" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Email</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              autoComplete="username"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full rounded-lg border border-ivory/15 bg-ink px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 focus:border-champagne focus:outline-none transition-colors"
              placeholder="admin email"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Password</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="w-full rounded-lg border border-ivory/15 bg-ink px-4 py-3 text-sm text-ivory placeholder:text-ivory/30 focus:border-champagne focus:outline-none transition-colors"
              placeholder="password"
            />
          </div>
          {error && (
            <p className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-2.5 text-xs text-red-300" data-testid="admin-login-error" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-champagne px-8 py-3.5 text-sm font-semibold tracking-wide text-ink transition-colors hover:bg-ivory disabled:opacity-60"
          >
            <Lock className="h-4 w-4" />
            {loading ? "Signing in…" : "Sign In"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-ivory/35">
          <a href="/" data-testid="admin-login-back" className="transition-colors hover:text-champagne">← Back to website</a>
        </p>
      </div>
    </main>
  );
}
